import React, { useState, useEffect } from 'react';
import { Wifi, WifiOff, RefreshCw } from 'lucide-react';

export const OfflineIndicator = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [showIndicator, setShowIndicator] = useState(false);

  useEffect(() => {
    const handleOnline = () => {
      console.log('🟢 Online');
      setIsOnline(true);
      setShowIndicator(true);
      
      // Ocultar indicador después de 3 segundos
      setTimeout(() => setShowIndicator(false), 3000);
    };

    const handleOffline = () => {
      console.log('🔴 Offline');
      setIsOnline(false);
      setShowIndicator(true);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Mostrar indicador si estamos offline al cargar
    if (!navigator.onLine) {
      setShowIndicator(true);
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  if (!showIndicator) return null;

  return (
    <div
      className={`fixed top-4 right-4 z-50 flex items-center gap-2 px-4 py-2 rounded-lg shadow-lg text-white text-sm font-medium transition-all duration-300 ${
        isOnline 
          ? 'bg-green-600 animate-slide-in' 
          : 'bg-orange-600'
      }`}
    >
      {isOnline ? (
        <>
          <Wifi className="w-4 h-4" />
          <span>Conexión restaurada</span>
        </>
      ) : (
        <div className="flex flex-col">
          <div className="flex items-center gap-2">
            <WifiOff className="w-4 h-4 animate-pulse" />
            <span className="font-bold">Modo Offline</span>
          </div>
          <span className="text-xs opacity-90 mt-1">
            📚 Biblioteca • 📅 Eventos • 📝 Peticiones disponibles
          </span>
        </div>
      )}
    </div>
  );
};

export const InstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);

  useEffect(() => {
    const handleBeforeInstallPrompt = (e) => {
      // Prevenir que Chrome muestre su prompt automáticamente
      e.preventDefault();
      setDeferredPrompt(e);
      
      // Verificar si ya está instalada
      if (window.matchMedia('(display-mode: standalone)').matches) {
        console.log('✅ App ya instalada');
        return;
      }
      
      // Mostrar nuestro prompt personalizado
      setShowInstallPrompt(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Detectar si la app está instalada
    if (window.matchMedia('(display-mode: standalone)').matches) {
      console.log('✅ Ejecutando como PWA instalada');
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;

    // Mostrar el prompt de instalación
    deferredPrompt.prompt();

    // Esperar a que el usuario responda
    const { outcome } = await deferredPrompt.userChoice;
    
    console.log(`Usuario ${outcome === 'accepted' ? 'aceptó' : 'rechazó'} la instalación`);

    // Limpiar el prompt
    setDeferredPrompt(null);
    setShowInstallPrompt(false);
  };

  const handleDismiss = () => {
    setShowInstallPrompt(false);
    // Guardar en localStorage para no mostrar de nuevo por un tiempo
    localStorage.setItem('installPromptDismissed', Date.now().toString());
  };

  // No mostrar si fue rechazado recientemente (últimas 24h)
  const dismissedTime = localStorage.getItem('installPromptDismissed');
  if (dismissedTime && (Date.now() - parseInt(dismissedTime)) < 86400000) {
    return null;
  }

  if (!showInstallPrompt) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 z-50 bg-gradient-to-r from-cyan-600 to-blue-600 text-white rounded-lg shadow-2xl p-4 animate-slide-up">
      <div className="flex items-start gap-3">
        <div className="bg-white/20 rounded-full p-2">
          <RefreshCw className="w-6 h-6" />
        </div>
        
        <div className="flex-1">
          <h3 className="font-bold text-lg mb-1">
            ¡Instala la App! 📱
          </h3>
          <p className="text-sm text-white/90 mb-3">
            Accede más rápido y trabaja sin conexión durante tus eventos
          </p>
          
          <div className="flex gap-2">
            <button
              onClick={handleInstallClick}
              className="px-4 py-2 bg-white text-cyan-600 rounded-lg font-medium text-sm hover:bg-white/90 transition-colors"
            >
              Instalar
            </button>
            <button
              onClick={handleDismiss}
              className="px-4 py-2 bg-white/20 rounded-lg font-medium text-sm hover:bg-white/30 transition-colors"
            >
              Ahora no
            </button>
          </div>
        </div>
        
        <button
          onClick={handleDismiss}
          className="text-white/70 hover:text-white"
        >
          ✕
        </button>
      </div>
    </div>
  );
};
